import React, { useState, useMemo } from 'react';
import { useStock, useWarehouses } from '../api/hooks';
import { useQueryClient } from '@tanstack/react-query';
import Papa from 'papaparse';

function LeftoverProducts() {
  const queryClient = useQueryClient();
  const { data: stockData = [], isLoading: loadingStock, refetch: refetchStock } = useStock();
  const { data: warehouses = [], isLoading: loadingWarehouses } = useWarehouses();

  const [search, setSearch] = useState('');
  const [selectedWarehouse, setSelectedWarehouse] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('CurrentQuantity');
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState(null);
  const itemsPerPage = 25;

  const loading = loadingStock || loadingWarehouses;

  // ДІАГНОСТИКА: Логуємо що приходить з API
  React.useEffect(() => {
    console.log('=== ДІАГНОСТИКА ЗАЛИШКІВ ===');
    console.log('stockData length:', stockData.length);
    console.log('warehouses length:', warehouses.length);
    if (stockData.length > 0) {
      console.log('Перший товар:', stockData[0]);
      const redItems = stockData.filter(item => item.Status === 'red');
      const yellowItems = stockData.filter(item => item.Status === 'yellow');
      console.log('Критичних (red):', redItems.length);
      console.log('Ризикових (yellow):', yellowItems.length);
      if (redItems.length > 0) {
        console.log('Приклад критичного товару:', redItems[0]);
      }
    }
  }, [stockData, warehouses]);

  const warehouseMap = useMemo(() =>
    warehouses.reduce((map, w) => ({ ...map, [w.storehouse_id]: w.name }), {}),
  [warehouses]);

  const allCategories = useMemo(() =>
    Array.from(new Set(stockData.map(item => item.Category))).sort(),
  [stockData]);

  const allSuppliers = useMemo(() => {
    const names = new Set();
    stockData.forEach(item => {
      let list = [];
      if (Array.isArray(item.Suppliers)) {
        list = item.Suppliers.filter(s => s && s.trim()).map(s => s.trim());
      } else if (typeof item.Suppliers === 'string' && item.Suppliers.trim()) {
        list = item.Suppliers.split(',').filter(s => s && s.trim()).map(s => s.trim());
      }
      
      if (list.length === 0) {
        names.add('Не вказано');
      } else {
        list.forEach(s => names.add(s));
      }
    });
    const sorted = Array.from(names).sort((a, b) => a.localeCompare(b));
    return ['', 'Не вказано', ...sorted.filter(s => s !== 'Не вказано')];
  }, [stockData]);

  const processedData = useMemo(() => {
    const processed = stockData.map(item => {
      const warehouseName = warehouseMap[item.storehouse_id] || '';
      
      // ✅ Визначаємо alertType на основі Status з API (не з dashboard!)
      let alertType = null;
      if (item.Status === 'deadstock') {
        alertType = 'deadstock';
      } else if (item.Status === 'red') {
        alertType = 'critical';
      } else if (item.Status === 'yellow') {
        alertType = 'warning';
      }
      
      let suppliersList = [];
      if (Array.isArray(item.Suppliers)) {
        suppliersList = item.Suppliers.filter(s => s && s.trim()).map(s => s.trim());
      } else if (typeof item.Suppliers === 'string' && item.Suppliers.trim()) {
        suppliersList = item.Suppliers.split(',').filter(s => s && s.trim()).map(s => s.trim());
      }
      
      if (suppliersList.length === 0) {
        suppliersList = ['Не вказано'];
      }
      
      const suppliersText = suppliersList.join(', ');
      
      // Мапінг нових полів з backend для сумісності
      return { 
        ...item, 
        warehouseName, 
        alertType, 
        suppliersList, 
        suppliersText,
        Forecast1Week: item.QuantityAfter1Week,  // Залишок після 1 тижня
        Forecast2Weeks: item.QuantityAfter2Weeks  // Залишок після 2 тижнів
      };
    });
    
    // ДІАГНОСТИКА: Перевіряємо alertType після обробки
    const criticalItems = processed.filter(item => item.alertType === 'critical');
    const warningItems = processed.filter(item => item.alertType === 'warning');
    console.log('=== PROCESSED DATA ===');
    console.log('Всього оброблено:', processed.length);
    console.log('З alertType critical:', criticalItems.length);
    console.log('З alertType warning:', warningItems.length);
    if (criticalItems.length > 0) {
      console.log('Приклад critical:', criticalItems[0]);
    }
    
    return processed;
  }, [stockData, warehouseMap]);

  const filteredContent = useMemo(() => {
    const searchMatch = search.trim().toLowerCase();
    const filtered = processedData
      .filter(item =>
        (item.Product?.toLowerCase().includes(searchMatch) ||
         String(item.ProductID).includes(searchMatch)) &&
        (!selectedWarehouse || item.warehouseName === selectedWarehouse) &&
        (!selectedCategory || item.Category === selectedCategory) &&
        (!selectedSupplier || item.suppliersList.includes(selectedSupplier)) &&
        (selectedTag === 'all' || item.alertType === selectedTag)
      )
      .sort((a, b) => {
        const aVal = a[sortField];
        const bVal = b[sortField];
        if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    
    // ДІАГНОСТИКА: Логуємо результат фільтрації
    console.log('=== ФІЛЬТРАЦІЯ ===');
    console.log('processedData:', processedData.length);
    console.log('selectedTag:', selectedTag);
    console.log('selectedWarehouse:', selectedWarehouse);
    console.log('selectedCategory:', selectedCategory);
    console.log('filtered:', filtered.length);
    
    return filtered;
  }, [processedData, search, selectedWarehouse, selectedCategory, selectedSupplier, selectedTag, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(filteredContent.length / itemsPerPage));
  const paginatedData = filteredContent.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSort = field => {
    setSortField(field);
    setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const getRowColor = (status, alertType) =>
    alertType === 'deadstock' ? 'bg-gray-200' :
    status === 'red' ? 'bg-red-50' :
    status === 'yellow' ? 'bg-yellow-50' :
    status === 'green' ? 'bg-green-50' : '';

  const handleExport = () => {
    const csv = Papa.unparse(filteredContent.map(item => ({
      ProductID: item.ProductID,
      Product: item.Product,
      Category: item.Category,
      Leftover: item.CurrentQuantity,
      Forecast_1Week: item.Forecast1Week,
      Forecast_2Weeks: item.Forecast2Weeks,
      Warehouse: item.warehouseName,
      Suppliers: item.suppliersText,
      Alert: item.alertType
    })));
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'leftovers.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRefresh = async () => {
    // Очищаємо кеш і перезавантажуємо дані
    await queryClient.invalidateQueries(['stock']);
    await refetchStock();
  };

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-3">Залишки продуктів</h2>
      <div className="flex gap-2 mb-3 flex-wrap sm:flex-nowrap">
        <input
          type="text"
          placeholder="Пошук..."
          className="border px-2 py-1 rounded text-xs w-36"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          value={selectedWarehouse}
          onChange={e => setSelectedWarehouse(e.target.value)}
          className="border px-2 py-1 rounded text-xs w-36"
        >
          <option value="">Усі склади</option>
          {warehouses.map(w => (
            <option key={w.storehouse_id} value={w.name}>{w.name}</option>
          ))}
        </select>
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          className="border px-2 py-1 rounded text-xs w-36"
        >
          <option value="">Усі категорії</option>
          {allCategories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <select
          value={selectedSupplier}
          onChange={e => setSelectedSupplier(e.target.value)}
          className="border px-2 py-1 rounded text-xs w-36"
        >
          {allSuppliers.map(s => (
            <option key={s} value={s}>{s || 'Усі постачальники'}</option>
          ))}
        </select>
        <button onClick={handleExport} className="bg-blue-500 text-white px-3 py-1 text-xs rounded">Завантажити CSV</button>
      </div>
      <div className="flex gap-2 mb-3">
        {['all', 'critical', 'warning', 'deadstock'].map(tag => (
          <button
            key={tag}
            onClick={() => setSelectedTag(tag)}
            className={`text-xs px-3 py-1 rounded ${selectedTag === tag ? (tag === 'critical' ? 'bg-red-500 text-white' : tag === 'warning' ? 'bg-yellow-500 text-white' : tag === 'deadstock' ? 'bg-gray-600 text-white' : 'bg-blue-500 text-white') : 'bg-gray-200'}`}
          >
            {tag === 'all' ? 'Всі' : tag === 'critical' ? 'Критичні' : tag === 'warning' ? 'Увага' : 'Запаси без руху'}
          </button>
        ))}
      </div>
      {!loading && (
        <>
          <table className="table-auto w-full text-xs border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th onClick={()=>toggleSort('ProductID')} className="py-1 px-1 border cursor-pointer">ID ↑↓</th>
                <th onClick={()=>toggleSort('Product')} className="py-1 px-1 border cursor-pointer">Продукт ↑↓</th>
                <th onClick={()=>toggleSort('CurrentQuantity')} className="py-1 px-1 border text-center cursor-pointer">Залишки ↑↓</th>
                <th onClick={()=>toggleSort('Forecast1Week')} className="py-1 px-1 border text-center cursor-pointer">+1 Тиж. ↑↓</th>
                <th onClick={()=>toggleSort('Forecast2Weeks')} className="py-1 px-1 border text-center cursor-pointer">+2 Тижні ↑↓</th>
                <th onClick={()=>toggleSort('warehouseName')} className="py-1 px-1 border cursor-pointer">Склад ↑↓</th>
                <th onClick={()=>toggleSort('Category')} className="py-1 px-1 border cursor-pointer">Категорія ↑↓</th>
                <th onClick={()=>toggleSort('suppliersText')} className="py-1 px-1 border cursor-pointer">Постачальник ↑↓</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, idx)=>(
                <React.Fragment key={idx}>
                  <tr onClick={()=>setExpandedRow(expandedRow===idx?null:idx)} className={`border-t cursor-pointer hover:bg-gray-50 ${getRowColor(item.Status,item.alertType)}`}>
                    <td className="py-1 px-1 border">{item.ProductID}</td>
                    <td className="py-1 px-1 border whitespace-normal break-words">{item.Product}</td>
                    <td className="py-1 px-1 border text-center">{item.CurrentQuantity}</td>
                    <td className="py-1 px-1 border text-center">{item.Forecast1Week}</td>
                    <td className="py-1 px-1 border text-center">{item.Forecast2Weeks}</td>
                    <td className="py-1 px-1 border">{item.warehouseName}</td>
                    <td className="py-1 px-1 border">{item.Category}</td>
                    <td className="py-1 px-1 border max-w-[180px]">
                      {item.suppliersList.length > 0 && item.suppliersList[0] !== 'Не вказано' ? (
                        <div className="flex flex-col items-start gap-0.5">
                          {item.suppliersList.map((s, sIdx) => (
                            <span 
                              key={sIdx} 
                              className={sIdx === 0 ? "font-semibold text-blue-600" : "text-gray-500 text-[10px]"}
                              title={sIdx === 0 ? "Найчастіший постачальник за 6 міс." : ""}
                            >
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">Не вказано</span>
                      )}
                    </td>
                  </tr>
                  {expandedRow===idx && (
                    <tr className="bg-blue-50">
                      <td colSpan="8" className="px-2 py-2 text-xs text-gray-700">
                        <strong>Постачальник:</strong> {item.suppliersText}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className="flex justify-between items-center mt-4 text-xs">
            <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1} className="bg-gray-200 px-2 py-1 rounded disabled:opacity-50">← Попередня</button>
            <div className="flex items-center gap-2">
              <span>Сторінка</span>
              <input
                type="number"
                value={currentPage}
                min={1}
                max={totalPages}
                onChange={e=>setCurrentPage(Math.min(totalPages,Math.max(1,parseInt(e.target.value)||1)))}
                className="border rounded px-2 py-1 w-12 text-center"
              />
              <span>з {totalPages}</span>
            </div>
            <button onClick={()=>setCurrentPage(p=>Math.min(p+1,totalPages))} disabled={currentPage===totalPages} className="bg-gray-200 px-2 py-1 rounded disabled:opacity-50">Наступна →</button>
          </div>
        </>)}
    </div>
  );
}

export default LeftoverProducts;